import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { jobApi } from '@/api/endpoints'
import { cn } from '@/lib/utils'
import type { AutoApplyFilters, JobFilterChoices } from '@/types/api'
import { boardOptionId, sourceFilterFor, sourceOptionId } from '../sourceFilter'
import { filterChips, sourceLabel } from './filters'

type Option = { value: string; label: string }

/** The "pull jobs from" list (same query as Dashboard and Browse), plus company career pages. */
export function useSourceOptions() {
  const sourcesQuery = useQuery({
    queryKey: ['job-sources'],
    queryFn: jobApi.sources,
    staleTime: 30_000,
  })
  return useMemo(() => {
    const listed = Array.isArray(sourcesQuery.data) ? sourcesQuery.data : []
    const options: Option[] = listed
      .filter((option) => option.available)
      .map((option) => ({ value: sourceOptionId(option), label: option.label }))
    if (!options.some((option) => option.value === 'internal')) {
      options.push({ value: 'internal', label: 'Company careers' })
    }
    // bySource and chips use bare keys: a source name or a portal key.
    const labels: Record<string, string> = {}
    for (const option of listed) labels[option.board ?? option.source] = option.label
    labels.internal = labels.internal ?? 'Company careers'
    return { options, labels }
  }, [sourcesQuery.data])
}

export function useFilterChoices(): JobFilterChoices | undefined {
  return useQuery({
    queryKey: ['job-filter-choices'],
    queryFn: jobApi.filterChoices,
    staleTime: Infinity,
  }).data
}

/** Where jobs came from: a native select styled as a chip, so phones get their own picker. */
export function SourceChip({
  filters,
  options,
  onChange,
}: {
  filters: AutoApplyFilters
  options: Option[]
  onChange: (next: AutoApplyFilters) => void
}) {
  const value = filters.boards?.[0] ? boardOptionId(filters.boards[0]) : filters.sources?.[0] ?? ''
  const selected = options.find((option) => option.value === value)
  const label = selected?.label ?? (value ? sourceLabel(filters.boards?.[0] ?? filters.sources?.[0] ?? '') : '')
  return (
    <label
      className={cn(
        'relative inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-pill border pl-3 pr-2.5 text-[13px] transition-colors',
        value ? 'border-ink bg-ink text-oncolor' : 'border-line bg-surface text-ink-soft hover:border-line-strong hover:text-ink',
      )}
    >
      <span className="max-w-[180px] truncate">{value ? `Source: ${label}` : 'Source'}</span>
      <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="m3 4.5 3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
      <select
        value={value}
        onChange={(event) => {
          const { sources, boards } = sourceFilterFor(event.target.value || null)
          onChange({ ...filters, sources, boards })
        }}
        aria-label="Source"
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        <option value="">All sources</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  )
}

/** "Match score ━━━●━━ 70%+": the minimum match, defaulting to the user's own. */
export function MatchScoreControl({
  value,
  defaultValue,
  onChange,
}: {
  value: number | undefined
  defaultValue: number
  onChange: (next: number | undefined) => void
}) {
  const current = value ?? defaultValue
  return (
    <label className="flex min-w-0 items-center gap-3 text-[13px]">
      <span className="shrink-0 font-medium text-ink-soft">Match score</span>
      <input
        type="range"
        min={30}
        max={95}
        step={5}
        value={current}
        onChange={(event) => {
          const next = Number(event.target.value)
          // Your own minimum is the default: no chip for it.
          onChange(next === defaultValue ? undefined : next)
        }}
        aria-label="Minimum match score"
        aria-valuetext={`${current} percent or more`}
        className="h-1.5 min-w-0 flex-1 cursor-pointer accent-ink sm:max-w-[220px]"
      />
      <span className="num w-[44px] shrink-0 text-right font-semibold text-ink">{current}%+</span>
    </label>
  )
}

/** Every filter as a removable chip, then Clear all. */
export function ActiveFilters({
  filters,
  choices,
  sourceLabels,
  defaultScore,
  onChange,
  onClear,
}: {
  filters: AutoApplyFilters
  choices?: JobFilterChoices
  sourceLabels?: Record<string, string>
  defaultScore: number
  onChange: (next: AutoApplyFilters) => void
  onClear: () => void
}) {
  const chips = filterChips(filters, { choices, sourceLabels, defaultScore })
  if (!chips.length) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="Active filters">
      {chips.map((chip) => (
        <span
          key={chip.id}
          className="inline-flex h-7 max-w-full items-center gap-1 rounded-pill border border-line bg-surface-muted pl-2.5 pr-1 text-[12.5px] text-ink"
        >
          <span className="max-w-[220px] truncate">{chip.label}</span>
          <button
            type="button"
            onClick={() => onChange(chip.remove(filters))}
            aria-label={`Remove ${chip.label}`}
            className="grid h-5 w-5 shrink-0 place-items-center rounded-pill text-ink-muted transition-colors hover:bg-line hover:text-ink"
          >
            <svg className="h-2.5 w-2.5" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="m3 3 6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClear}
        className="tap inline-flex h-7 items-center rounded-pill px-2 text-[12.5px] font-medium text-ink-muted underline-offset-2 transition-colors hover:text-ink hover:underline"
      >
        Clear all
      </button>
    </div>
  )
}
